<template>
  <div v-if="loading" class="text-center mt-5 font-2xl text-muted">
    <i class="fal fa-spinner fa-spin" /> Sample Analyte Result Table Data
    Loading...
  </div>
  <div v-else class="animated">
    <template v-if="sample_analyte_results && sample_analyte_results.length">
      <b-row align-h="end" class="mb-3">
        <b-col cols="2" class="pr-0">
          <b-button block @click="resetAll" variant="dark">
            <i class="fal fa-history" /> Reset All
          </b-button>
        </b-col>
        <b-col cols="2">
          <b-button variant="success" block @click="saveAll">
            <i class="fal fa-save" /> Save All
          </b-button>
        </b-col>
      </b-row>
      <b-table
        :items="sample_analyte_results"
        :fields="columns"
        :tbody-tr-class="(result) => generateClass(result, 'table')"
        responsive
        striped
        hover
        small
      >
        <template #cell(date)="data">
          {{ getResultData(data.item, "date", "created_at") }}
        </template>
        <template #cell(analyte)="data">
          <existence-gate
            :entity="data.item"
            :levels="['analyte']"
            :field="'name'"
          >
            <template #existing-content>
              <router-link
                :to="`/analytes/${getData(data.item, {
                  levels: ['analyte'],
                  field: 'id',
                })}`"
              >
                {{ formatAnalyteName(data.item.analyte) }}
              </router-link>
            </template>
          </existence-gate>
        </template>
        <template #cell(analysis)="data">
          <existence-gate
            :entity="data.item"
            :levels="['analysis']"
            :field="'name'"
          >
            <template #existing-content>
              <router-link
                :to="`/analysis/${getData(data.item, {
                  levels: ['analysis'],
                  field: 'id',
                })}`"
              >
                {{
                  formatAnalysisName(data.item.analysis)
                }}
              </router-link>
            </template>
          </existence-gate>
        </template>
        <template #cell(instrument_instance)="data">
          <existence-gate
            :entity="data.item"
            :levels="['instrument_instance']"
            :field="'name'"
          >
            <template #existing-content>
              <router-link
                :to="`/instrument-instances/${getData(data.item, {
                  levels: ['instrument_instance'],
                  field: 'id',
                })}`"
              >
                {{
                  getData(data.item, {
                    levels: ["instrument_instance"],
                    field: "name",
                  })
                }}
              </router-link>
            </template>
          </existence-gate>
        </template>
        <template #cell(jig)="data">
          {{ getResultData(data.item, "jig") }}
        </template>
        <template #cell(raw_result)="data">
          <b-form-radio-group
            v-if="
              data.item.analysis && data.item.analysis.name === 'Completion'
            "
            v-model="data.item.raw_result"
            :options="[
              { text: 'In Progress', value: null },
              { text: 'Done', value: 1 },
            ]"
            buttons
            button-variant="outline-dark"
            size="md"
            name="radio-btn-outline"
          />
          <b-input v-else type="number" v-model="data.item.raw_result" />
        </template>
        <template #cell(formatted_result)="data">
          <b-input
            v-if="
              !(data.item.analysis && data.item.analysis.name === 'Completion')
            "
            v-model="data.item.formatted_result"
          />
          <b-alert v-else class="py-2 m-0" show
            >No Formatted Value Required</b-alert
          >
        </template>
        <template #cell(unit_of_measure_id)="data">
          <b-select v-model="data.item.unit_of_measure_id">
            <b-select-option
              v-for="unit in getResultData(data.item, 'analysis', 'units')"
              :value="unit.id"
              :key="unit.id"
              >{{ unit.abbrev }}</b-select-option
            >
          </b-select>
        </template>
        <template #cell(status)="data">
          <b-select
            v-model="
              data.item.current_analyte_result_status_log
                .analyte_result_status_id
            "
          >
            <b-select-option
              v-for="status in status_collection.models"
              :value="status.id"
              :key="status.id"
              >{{ status.status }}</b-select-option
            >
          </b-select>
        </template>
        <template #cell(actions)="data">
          <b-button
            class="mr-2"
            variant="dark"
            @click="reset(data.item)"
            title="Reset Unsaved Changes"
          >
            <i class="fal fa-history" />
          </b-button>
          <b-button
            variant="success"
            title="Save Result"
            @click="save(data.item, true)"
          >
            <i class="fal fa-save" />
          </b-button>
        </template>
      </b-table>
    </template>
    <b-alert v-else show class="mb-4">No matching records.</b-alert>
  </div>
</template>
<script>
import moment from "moment";
import AnalyteResultModel from "@/models/lab/AnalyteResultModel";
import AnalyteResultStatusCollection from "@/collections/lab/AnalyteResultStatusCollection";
import ExistenceGate from "@/components/shared/ExistenceGate";
import ToastNotifications from "@/components/shared/mixins/ToastNotifications";
import { LabRepositoryFactory } from "@/repositories/lab/LabRepositoryFactory";
import {
  cloneAttributes,
  getEntityData,
  formatAnalysisName,
  formatAnalyteName
} from "@/shared/utils";

const AnalyteResultRepository = LabRepositoryFactory.get("analyteResult");

export default {
  name: "SampleEditableResultTable",
  components: {
    ExistenceGate,
  },
  mixins: [ToastNotifications],
  props: {
    status_collection: {
      type: Object,
      default: () => new AnalyteResultStatusCollection(),
    },
    task_type: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      loading: true,
      sample_id: this.$route.params.sample_id || null,
      sample_analyte_results: null,
      sample_analyte_results_original: null,
      columns: [
        { key: "date", label: "Date" },
        { key: "analysis", label: "Analysis" },
        { key: "analyte", label: "Analyte" },
        { key: "project_and_task", label: "Process & Task" },
        { key: "instrument_instance", label: "Lab Instrument" },
        { key: "jig", label: "Jig" },
        { key: "raw_result", label: "Raw" },
        { key: "formatted_result", label: "Formatted" },
        { key: "unit_of_measure_id", label: "Unit" },
        {
          key: "status",
          label: "Status",
          tdClass: (value, key, result) => this.generateClass(result, "bg"),
        },
        { key: "actions", label: "Actions" },
      ],
      table_data: {
        sample_type: ["analyte", "sample_type"],
      },
    };
  },
  methods: {
    getData(result, data) {
      return getEntityData({
        entity: result,
        ...data,
      });
    },
    formatAnalysisName(analysis) {
      return formatAnalysisName(analysis);
    },
    formatAnalyteName(analyte) {
      return formatAnalyteName(analyte);
    },
    generateClass(result, style) {
      const { analyte_result_status_id } =
        result.current_analyte_result_status_log;
      const status_match = this.status_collection.models.find(
        ({ id }) => id === analyte_result_status_id
      );
      const status = status_match && status_match.status;

      switch (status) {
        case "Pending":
          return `${style}-warning`;
        case "Approved":
          return `${style}-success`;
        case "Declined":
          return `${style}-danger`;
        default:
          return "";
      }
    },
    reset({ id }) {
      const origial = this.sample_analyte_results_original.find(
        (result) => id === result.id
      );
      this.sample_analyte_results = this.sample_analyte_results.map(
        (result) => {
          return result.id === id ? this.lodash.cloneDeep(origial) : result;
        }
      );
    },
    resetAll() {
      this.sample_analyte_results = this.lodash.cloneDeep(
        this.sample_analyte_results_original
      );
    },
    async save(result, single = false) {
      const payload = {
        ...cloneAttributes(result, true, true, [
          "id",
          "created_by",
          "updated_by",
        ]),
      };

      const model = new AnalyteResultModel(result);
      const new_status_id =
        result.current_analyte_result_status_log.analyte_result_status_id;

      model.setStatus(new_status_id);

      try {
        await AnalyteResultRepository.update(result.id, payload);
        single &&
          this.quickSuccessToast(
            "Result Saved",
            "Sample result data was updated successfully."
          );
      } catch ({ response }) {
        const { status, data } = response;

        this.$bvToast.toast(`Code: ${status}. ${data.error}`, {
          title: "Result Save Error.",
          variant: "danger",
        });
      }
    },
    async saveAll() {
      await this.sample_analyte_results.forEach((result) => this.save(result));

      this.quickSuccessToast(
        "Results Saved",
        "Sample results data was updated successfully."
      );
    },
    getResultData(result, field, value) {
      const { instrument_jig, batch_index, jig_index } = result;

      switch (field) {
        case "date":
          return moment(result[value]).format("lll");
        case "jig":
          return `${
            instrument_jig ? instrument_jig.jig.name : "No Jig"
          }(${batch_index}/${jig_index})`;
        case "status":
          return result.current_analyte_result_status_log.analyte_result_status
            .status;
        case "raw_result":
        case "formatted_result":
          return result[field];
        default:
          return result[field] && result[field][value];
      }
    },
    async init() {
      const connections = [
        "analyte.sample_type",
        "analyte_result_status_logs",
        "analysis.analyte.sample_type",
        "analysis.units",
        "current_analyte_result_status_log.analyte_result_status",
        "instrument_jig.jig",
        "instrument_instance.instrument",
        // `schedulable_task.${this.task_type.toLowerCase()}_process`,
        "unit_of_measure",
      ]
        .map((connection) => `connection[]=${connection}`)
        .join("&");
      const filter = `filter[]=sample_id:eq:${this.sample_id}:where&filter[]=schedulable_task_type:ct:${this.task_type}:where`;

      const { data } = await AnalyteResultRepository.getSampleResults({
        connections,
        filter,
      });

      this.sample_analyte_results = this.lodash.cloneDeep(data.data);
      this.sample_analyte_results_original = this.lodash.cloneDeep(data.data);
      this.loading = false;
    },
  },
  created() {
    this.loading = true;

    this.init();
  },
};
</script>
